import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  MenuList,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CompareArrows,
  Delete,
  FindInPage,
  Fingerprint,
  History,
  Link,
  MoreVert,
  NotificationsActive,
  NotificationsNone,
  Rule,
  Settings,
  Upload,
} from '@mui/icons-material';

import {
  ILegalClientDocumentFile,
  ILegalClientDocumentTypeEntry,
  ILegalClientDocumentVersion,
} from '../../../Interfaces/ILegalClientDocumentVersion';
import {
  GWG_REQUIREMENT_FOLDER_DEFAULT,
  IGwGDocumentType,
  IGwGFolderSaveObject,
  IGwGParticipant,
  describeReminder,
} from '../../../Interfaces/IGwGFolder';
import { ILegalTransactionMeeting } from '../../../Interfaces/ILegalTransactionMeeting';
import { DocumentVersionCompareDialog } from '../../../Clients/DocumentTab/DocumentVersionCompareDialog';
import { DocumentVersionPreviewDialog } from '../../../Clients/DocumentTab/DocumentVersionPreviewDialog';
import { DocumentVersionUploadDialog } from '../../../Clients/DocumentTab/DocumentVersionUploadDialog';
import {
  IGwGFolderState,
  IGwGLegalPerson,
  addVersion,
  clearParticipantOverride,
  describeCompleteness,
  removeVersion,
  setDefaultMandatory,
  setParticipantMandatory,
  setReminderArray,
} from './gwgFolderState';
import { GwGReminderDialog } from './GwGReminderDialog';

interface IProps {
  state: IGwGFolderState;
  setState: (state: IGwGFolderState) => void;
  meetingArray: ILegalTransactionMeeting[];
  isCalendarEnabled: boolean;
  isReadOnly: boolean;
  /**
   * Beteiligte juristische Personen des Vorgangs. Bewusst als eigene Angabe und
   * nicht aus `DocumentTypeArray` abgeleitet: solange keine Dokumentart
   * konfiguriert ist, hat die berechnete Sicht keine Zeilen — daraus liesse sich
   * sonst falsch schliessen, es sei niemand beteiligt.
   */
  legalPersonArray: IGwGLegalPerson[];
  /** Konfigurationsdialog des Ordners oeffnen. */
  onOpenConfig: () => void;
}

/** Zeile der Ansicht als Dokumenttyp-Eintrag — so nehmen die Dialoge sie an. */
const toTypeEntry = (
  typeEntry: IGwGDocumentType,
  participant: IGwGParticipant
): ILegalClientDocumentTypeEntry => ({
  idLegalClientDocumentType: typeEntry.idLegalClientDocumentType,
  LegalClientDocumentType: typeEntry.LegalClientDocumentType,
  VersionArray: participant.VersionArray,
});

const describeVersion = (
  version: ILegalClientDocumentVersion | null | undefined
): string => {
  if (version === null || version === undefined) {
    return 'Kein Dokument';
  }

  const fileCount = version.FileArray.length;

  if (fileCount === 0) {
    return 'Kein Dokument';
  }

  return fileCount === 1
    ? `Version ${version.VersionNumber}`
    : `Version ${version.VersionNumber} (${fileCount} Dateien)`;
};

interface IMenuTarget {
  typeEntry: IGwGDocumentType;
  participant: IGwGParticipant;
  /** Nur beim Aufruf über das Menü gesetzt, nicht bei den direkten Symbolen. */
  anchor?: HTMLElement;
}

/**
 * GwG-Ordner: Arbeitsoberfläche für die GwG-Kontrollstrukturen eines Vorgangs.
 *
 * Gruppiert nach Dokumentart, darunter eine Zeile je beteiligter juristischer
 * Person. Die Dokumente selbst liegen beim jeweiligen Mandanten — hier werden sie
 * nur durchgereicht und um Pflicht-/Optional-Status, Erinnerungen und die
 * Vollständigkeitskontrolle ergänzt. Ein Upload landet direkt beim Mandanten,
 * eine dort hochgeladene Version erscheint hier unmittelbar.
 *
 * Wie überall im Vorgang wirkt alles erst mit dem Speichern.
 */
export const GwGFolderContent: React.FC<IProps> = props => {
  const [menuTarget, setMenuTarget] = useState<IMenuTarget | null>(null);
  const [uploadTarget, setUploadTarget] = useState<IMenuTarget | null>(null);
  const [previewTarget, setPreviewTarget] = useState<IMenuTarget | null>(null);
  const [historyTarget, setHistoryTarget] = useState<IMenuTarget | null>(null);
  const [compareTarget, setCompareTarget] = useState<IMenuTarget | null>(null);
  const [reminderTarget, setReminderTarget] = useState<{
    typeEntry: IGwGDocumentType;
    participant: IGwGParticipant | null;
  } | null>(null);

  const folder = props.state.folder;
  const canModify = !props.isReadOnly;

  const closeMenu = () => setMenuTarget(null);

  /**
   * Aenderung am Speicherobjekt melden. Die Anzeige wird davon abgeleitet und
   * nicht hier fortgeschrieben — `buildFolderView()` rechnet sie beim naechsten
   * Render neu.
   */
  const applyChange = (
    change: (saveObject: IGwGFolderSaveObject) => IGwGFolderSaveObject
  ) => {
    props.setState({
      folder: props.state.folder,
      saveObject: change(props.state.saveObject),
    });
    closeMenu();
  };

  /** Kopfzeile mit Ordnernamen und Konfigurations-Schaltflaeche. */
  const renderHeader = (rightSlot?: React.ReactNode) => (
    <Stack
      direction='row'
      alignItems='center'
      justifyContent='space-between'
      sx={{ mb: 2 }}
    >
      <Typography variant='subtitle1'>
        <Fingerprint
          fontSize='small'
          sx={{ mr: 1, verticalAlign: 'text-bottom' }}
        />
        {folder.Title ?? 'GwG'}
      </Typography>
      <Stack direction='row' spacing={1} alignItems='center'>
        {rightSlot}
        <Button
          size='small'
          variant='outlined'
          startIcon={<Settings />}
          disabled={!canModify}
          onClick={props.onOpenConfig}
        >
          Konfiguration
        </Button>
      </Stack>
    </Stack>
  );

  if (props.legalPersonArray.length === 0) {
    return (
      <Paper variant='outlined' sx={{ p: 2 }}>
        {renderHeader()}
        <Alert severity='info'>
          Am Vorgang ist keine juristische Person beteiligt. Sobald eine
          hinzugefügt ist, erscheinen hier deren GwG-Unterlagen.
        </Alert>
      </Paper>
    );
  }

  if (folder.DocumentTypeArray.length === 0) {
    /**
     * Keine Dokumentart konfiguriert. Sobald eine hinterlegt ist, hat die
     * berechnete Sicht auch Zeilen — dieser Zweig deckt also wirklich nur den
     * unkonfigurierten Ordner ab.
     */
    return (
      <Paper variant='outlined' sx={{ p: 2 }}>
        {renderHeader()}
        <Alert severity='warning'>
          Für diesen Ordner ist noch keine Dokumentart festgelegt. Öffnen Sie
          die Konfiguration und wählen Sie, welche Unterlagen geprüft werden
          sollen.
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper variant='outlined' sx={{ p: 2, height: '100%', overflow: 'auto' }}>
      {renderHeader(
        <Chip
          size='small'
          color={folder.Completeness.isComplete ? 'success' : 'warning'}
          label={describeCompleteness(folder)}
        />
      )}

      <Alert severity='info' sx={{ mb: 2 }} icon={<Rule fontSize='inherit' />}>
        Die Unterlagen liegen beim jeweiligen Mandanten. Der GwG-Ordner zeigt
        sie vorgangsbezogen und überwacht die Vollständigkeit — er speichert
        keine Kopien. Erinnerungen gehen ausschließlich an die Systemgruppe GwG.
      </Alert>

      {folder.DocumentTypeArray.map(typeEntry => (
        <Box
          key={`gwg-type-${typeEntry.idLegalClientDocumentType}`}
          sx={{ mb: 3 }}
        >
          <Stack
            direction='row'
            alignItems='center'
            spacing={1}
            sx={{ mb: 0.5 }}
          >
            <Typography variant='subtitle2'>
              {typeEntry.LegalClientDocumentType}
            </Typography>
            <Chip
              size='small'
              variant='outlined'
              color={typeEntry.isFulfilled ? 'success' : 'warning'}
              label={
                typeEntry.isFulfilled
                  ? 'Erfüllt'
                  : `Offen ${typeEntry.OpenCount}`
              }
            />
            <Box sx={{ flexGrow: 1 }} />
            <Tooltip
              title={
                typeEntry.isMandatoryDefault
                  ? 'Gilt als Pflichtunterlage — auf optional umstellen'
                  : 'Gilt als optional — auf Pflicht umstellen'
              }
            >
              <span>
                <Button
                  size='small'
                  variant='text'
                  disabled={!canModify}
                  onClick={() =>
                    applyChange(saveObject =>
                      setDefaultMandatory(
                        saveObject,
                        typeEntry.idLegalClientDocumentType,
                        !typeEntry.isMandatoryDefault
                      )
                    )
                  }
                >
                  {typeEntry.isMandatoryDefault
                    ? 'Standard: Pflicht'
                    : 'Standard: optional'}
                </Button>
              </span>
            </Tooltip>
            <Tooltip title='Erinnerungen für alle Beteiligten'>
              <IconButton
                size='small'
                onClick={() =>
                  setReminderTarget({ typeEntry: typeEntry, participant: null })
                }
              >
                {typeEntry.ReminderArray.length > 0 ? (
                  <NotificationsActive fontSize='small' color='primary' />
                ) : (
                  <NotificationsNone fontSize='small' />
                )}
              </IconButton>
            </Tooltip>
          </Stack>

          {typeEntry.ReminderArray.length > 0 && (
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ display: 'block', mb: 0.5 }}
            >
              Erinnerung{' '}
              {typeEntry.ReminderArray.map(describeReminder).join(' · ')}
            </Typography>
          )}

          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Beteiligter</TableCell>
                <TableCell>Dokument</TableCell>
                <TableCell>Anforderung</TableCell>
                <TableCell align='right'>Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {typeEntry.ParticipantArray.map(participant => (
                <TableRow
                  key={`gwg-row-${typeEntry.idLegalClientDocumentType}-${participant.idLegalClient}`}
                  hover
                >
                  <TableCell>{participant.LegalClient}</TableCell>
                  <TableCell>
                    <Typography
                      variant='body2'
                      color={
                        participant.hasDocument
                          ? 'text.primary'
                          : 'text.secondary'
                      }
                    >
                      {describeVersion(participant.CurrentVersion)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction='row' spacing={0.5} alignItems='center'>
                      <Chip
                        size='small'
                        variant={
                          participant.isMandatory ? 'filled' : 'outlined'
                        }
                        color={
                          participant.isMandatory && !participant.hasDocument
                            ? 'warning'
                            : 'default'
                        }
                        label={participant.isMandatory ? 'Pflicht' : 'Optional'}
                      />
                      {/* Bewusst der Vergleich mit der Vorgabe und nicht
                          `hasOverride`: dieses Feld sagt nur, dass für den
                          Beteiligten eine eigene Zeile existiert. Die bleibt
                          auch bestehen, wenn man den Status wieder auf die
                          Vorgabe zurückstellt — sie trägt zusätzlich seine
                          Erinnerungen. Der Chip behauptet aber eine Abweichung,
                          also muss er auch eine zeigen (QS #23, Punkt 12). Zum
                          Entfernen der Zeile selbst gibt es „Eigene Regel
                          aufheben" im Aktionsmenü. */}
                      {participant.isMandatory !==
                        typeEntry.isMandatoryDefault && (
                        <Tooltip
                          title={`Abweichend vom Standard des Ordners (${
                            typeEntry.isMandatoryDefault
                              ? 'Pflicht'
                              : 'optional'
                          })`}
                        >
                          <Chip
                            size='small'
                            variant='outlined'
                            label='eigene Regel'
                          />
                        </Tooltip>
                      )}
                      {participant.ReminderArray.length > 0 && (
                        <Tooltip
                          title={participant.ReminderArray.map(
                            describeReminder
                          ).join(' · ')}
                        >
                          <NotificationsActive
                            fontSize='small'
                            color='primary'
                          />
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align='right'>
                    {/* Reihenfolge und Symbole wie im uebrigen Vorgang:
                        `FindInPage` fuer die Vorschau (Document/DocumentRow),
                        `Link` fuer den Sprung zum Mandanten in einem neuen Tab
                        (LegalTransactionClientsOverview). */}
                    <Tooltip title='Vorschau'>
                      <span>
                        <IconButton
                          size='small'
                          disabled={!participant.hasDocument}
                          onClick={() =>
                            setPreviewTarget({
                              typeEntry: typeEntry,
                              participant: participant,
                            })
                          }
                        >
                          <FindInPage fontSize='small' />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title='Mandanten öffnen'>
                      <IconButton
                        size='small'
                        onClick={() =>
                          window.open(
                            `/clients/${participant.idLegalClient}`,
                            '_blank'
                          )
                        }
                      >
                        <Link fontSize='small' />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title='Weitere Aktionen'>
                      <IconButton
                        size='small'
                        onClick={(event: React.MouseEvent<HTMLElement>) =>
                          setMenuTarget({
                            typeEntry: typeEntry,
                            participant: participant,
                            anchor: event.currentTarget,
                          })
                        }
                      >
                        <MoreVert fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ))}

      <Menu
        open={menuTarget !== null}
        anchorEl={menuTarget?.anchor}
        onClose={closeMenu}
      >
        <MenuList dense>
          {/* „Aktuelle Version öffnen" steht als eigenes Symbol in der Zeile —
              hier nicht doppelt. */}
          <MenuItem
            disabled={!canModify}
            onClick={() => {
              setUploadTarget(menuTarget);
              closeMenu();
            }}
          >
            <Upload fontSize='small' sx={{ mr: 1 }} />
            Neue Version hochladen
          </MenuItem>
          <MenuItem
            disabled={(menuTarget?.participant.VersionArray.length ?? 0) === 0}
            onClick={() => {
              setHistoryTarget(menuTarget);
              closeMenu();
            }}
          >
            <History fontSize='small' sx={{ mr: 1 }} />
            Versionshistorie anzeigen
          </MenuItem>
          <MenuItem
            disabled={(menuTarget?.participant.VersionArray.length ?? 0) < 2}
            onClick={() => {
              setCompareTarget(menuTarget);
              closeMenu();
            }}
          >
            <CompareArrows fontSize='small' sx={{ mr: 1 }} />
            Versionen vergleichen
          </MenuItem>
          <MenuItem
            disabled={!canModify || menuTarget === null}
            onClick={() => {
              if (menuTarget === null) {
                return;
              }

              const target = menuTarget;

              applyChange(saveObject =>
                setParticipantMandatory(
                  saveObject,
                  target.typeEntry.idLegalClientDocumentType,
                  target.participant.idLegalClient,
                  !target.participant.isMandatory
                )
              );
            }}
          >
            <Rule fontSize='small' sx={{ mr: 1 }} />
            {menuTarget?.participant.isMandatory === true
              ? 'Für diesen Beteiligten optional'
              : 'Für diesen Beteiligten Pflicht'}
          </MenuItem>
          {menuTarget?.participant.hasOverride === true && (
            <MenuItem
              disabled={!canModify}
              onClick={() => {
                const target = menuTarget;

                applyChange(saveObject =>
                  clearParticipantOverride(
                    saveObject,
                    target.typeEntry.idLegalClientDocumentType,
                    target.participant.idLegalClient
                  )
                );
              }}
            >
              <Rule fontSize='small' sx={{ mr: 1 }} />
              Eigene Regel aufheben
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              if (menuTarget !== null) {
                setReminderTarget({
                  typeEntry: menuTarget.typeEntry,
                  participant: menuTarget.participant,
                });
              }

              closeMenu();
            }}
          >
            <NotificationsNone fontSize='small' sx={{ mr: 1 }} />
            Erinnerungen für diesen Beteiligten
          </MenuItem>
          <MenuItem
            disabled={
              !canModify || menuTarget?.participant.hasDocument !== true
            }
            onClick={() => {
              if (menuTarget === null) {
                return;
              }

              const target = menuTarget;

              applyChange(saveObject =>
                removeVersion(
                  saveObject,
                  target.typeEntry.idLegalClientDocumentType,
                  target.participant.idLegalClient,
                  target.participant.CurrentVersion
                    ?.idLegalClientDocumentVersion ?? null
                )
              );
            }}
          >
            <Delete fontSize='small' sx={{ mr: 1 }} />
            Aktuelle Version entfernen
          </MenuItem>
        </MenuList>
      </Menu>

      <DocumentVersionUploadDialog
        isOpen={uploadTarget !== null}
        setIsOpen={(isOpen: boolean) => {
          if (!isOpen) {
            setUploadTarget(null);
          }
        }}
        typeEntry={
          uploadTarget === null
            ? null
            : toTypeEntry(uploadTarget.typeEntry, uploadTarget.participant)
        }
        onSave={(
          idLegalClientDocumentType: number,
          fileArray: ILegalClientDocumentFile[],
          validUntil: string,
          note: string
        ) => {
          if (uploadTarget === null) {
            return;
          }

          const target = uploadTarget;

          props.setState({
            folder: props.state.folder,
            saveObject: addVersion(
              props.state.saveObject,
              idLegalClientDocumentType,
              target.participant.idLegalClient,
              fileArray,
              validUntil,
              note
            ),
          });
          setUploadTarget(null);
        }}
      />

      <DocumentVersionPreviewDialog
        isOpen={previewTarget !== null}
        setIsOpen={(isOpen: boolean) => {
          if (!isOpen) {
            setPreviewTarget(null);
          }
        }}
        version={previewTarget?.participant.CurrentVersion ?? undefined}
        typeName={`${previewTarget?.typeEntry.LegalClientDocumentType ?? ''} — ${
          previewTarget?.participant.LegalClient ?? ''
        }`}
      />

      <DocumentVersionCompareDialog
        isOpen={historyTarget !== null || compareTarget !== null}
        setIsOpen={(isOpen: boolean) => {
          if (!isOpen) {
            setHistoryTarget(null);
            setCompareTarget(null);
          }
        }}
        typeEntry={(() => {
          const target = compareTarget ?? historyTarget;

          return target === null
            ? null
            : toTypeEntry(target.typeEntry, target.participant);
        })()}
      />

      <GwGReminderDialog
        isOpen={reminderTarget !== null}
        setIsOpen={(isOpen: boolean) => {
          if (!isOpen) {
            setReminderTarget(null);
          }
        }}
        documentTypeName={
          reminderTarget?.typeEntry.LegalClientDocumentType ?? ''
        }
        participantName={reminderTarget?.participant?.LegalClient ?? null}
        reminderArray={
          reminderTarget === null
            ? []
            : (reminderTarget.participant?.ReminderArray ??
              reminderTarget.typeEntry.ReminderArray)
        }
        meetingArray={props.meetingArray}
        isCalendarEnabled={props.isCalendarEnabled}
        isReadOnly={!canModify}
        onSave={reminderArray => {
          if (reminderTarget === null) {
            return;
          }

          props.setState({
            folder: props.state.folder,
            saveObject: setReminderArray(
              props.state.saveObject,
              reminderTarget.typeEntry.idLegalClientDocumentType,
              reminderTarget.participant?.idLegalClient ??
                GWG_REQUIREMENT_FOLDER_DEFAULT,
              reminderArray
            ),
          });
          setReminderTarget(null);
        }}
      />
    </Paper>
  );
};
